import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ThemeProvider from "../theme/TemaMuiProvider";
import { Alert, Box, Button, CircularProgress, Paper, Snackbar, TextField, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import muiTheme from "../theme/muiTheme";
import logo from "../assets/logo.png";
import { FichaPessoaForm } from "../components/ficha/FichaPessoaForm";
import { aplicarFinsGarantia, obterGruposChecklistDocumentosFicha } from "../components/ficha/fichaChecklists";
import { fichaParaForm } from "../components/ficha/fichaFormato";
import { inputSx } from "../components/ficha/fichaEstilos";
import {
  CHECKLIST_FIADOR_PF,
  CHECKLIST_FIADOR_PJ,
  CHECKLIST_LOCATARIO_PF_IMOBILIARIA,
  CHECKLIST_LOCATARIO_PF_PADRAO,
  CHECKLIST_LOCATARIO_PJ_PADRAO,
} from "../components/ficha/fichaChecklists";

// Tela pública (sem login) em que Locatária, Fiador ou Seguradora preenchem a
// própria Ficha de Pessoa. Renderiza o MESMO <FichaPessoaForm> da tela
// autenticada — é para isso que ele foi extraído.
//
// Diferenças de propósito em relação à tela interna:
//   • `ro={false}` e `ehAprovador={false}` — a pessoa externa nunca vê o realce
//     de "campo vazio" do aprovador, e nunca cai no modo somente-leitura por
//     permissão (o que fecha o acesso aqui é a janela de status, no backend);
//   • sem abas, sem grade, sem dados das outras partes nem da proposta;
//   • `fetch` cru, nunca services/api.js — não pode mandar Authorization.

const EMPTY_DOCS = [];
const EMPTY_UPLOAD_STATE = {};
const EMPTY_ERROS = {};

const BASE = "/api/ficha-publica";

const CHAVE_CACHE = (token) => `imobgest_fc_rascunho_${token}`;

function gravarCache(token, formulario, justificativas) {
  if (!formulario) return false;

  try {
    localStorage.setItem(
      CHAVE_CACHE(token),
      JSON.stringify({
        formulario,
        justificativas,
        salvoEm: new Date().toISOString(),
      }),
    );

    return true;
  } catch (erro) {
    console.error("Erro ao salvar cache da FC Externa:", erro);
    return false;
  }
}

function recuperarCache(token) {
  try {
    const conteudo = localStorage.getItem(CHAVE_CACHE(token));
    return conteudo ? JSON.parse(conteudo) : null;
  } catch {
    return null;
  }
}

function limparCache(token) {
  localStorage.removeItem(CHAVE_CACHE(token));
}

const BANCO_DOCS_LOCAL = "imobgest_fc_documentos_locais";
const STORE_DOCS_LOCAL = "arquivos";

function abrirBancoDocsLocal() {
  return new Promise((resolve, reject) => {
    const pedido = indexedDB.open(BANCO_DOCS_LOCAL, 1);

    pedido.onupgradeneeded = () => {
      const banco = pedido.result;

      if (!banco.objectStoreNames.contains(STORE_DOCS_LOCAL)) {
        banco.createObjectStore(STORE_DOCS_LOCAL, {
          keyPath: "id",
        });
      }
    };

    pedido.onsuccess = () => resolve(pedido.result);
    pedido.onerror = () => reject(pedido.error);
  });
}

async function executarBancoDocsLocal(modo, operacao) {
  const banco = await abrirBancoDocsLocal();

  try {
    return await new Promise((resolve, reject) => {
      const transacao = banco.transaction(STORE_DOCS_LOCAL, modo);
      const store = transacao.objectStore(STORE_DOCS_LOCAL);

      let resultado;
      let pedido;

      try {
        pedido = operacao(store);
      } catch (erro) {
        reject(erro);
        return;
      }

      if (pedido) {
        pedido.onsuccess = () => {
          resultado = pedido.result;
        };
      }

      transacao.oncomplete = () => resolve(resultado);
      transacao.onerror = () => reject(transacao.error);
      transacao.onabort = () => reject(transacao.error || new Error("Transação cancelada."));
    });
  } finally {
    banco.close();
  }
}

async function salvarDocumentoLocal(documento) {
  await executarBancoDocsLocal("readwrite", (store) => store.put(documento));
}

async function buscarDocumentoLocal(id) {
  return executarBancoDocsLocal("readonly", (store) => store.get(String(id)));
}

async function excluirDocumentoLocal(id) {
  await executarBancoDocsLocal("readwrite", (store) => store.delete(String(id)));
}

async function listarDocumentosLocais(token) {
  const todos = await executarBancoDocsLocal("readonly", (store) => store.getAll());

  return todos.filter((documento) => documento.token === token);
}

async function limparDocumentosLocais(token) {
  const documentos = await listarDocumentosLocais(token);

  for (const documento of documentos) {
    await excluirDocumentoLocal(documento.id);
  }
}

export default function PreencherFichaPublica() {
  const { token } = useParams();

  // ── Segundo fator (N primeiros dígitos do CPF/CNPJ da própria ficha) ──────
  const [chave, setChave] = useState("");
  const [sessao, setSessao] = useState("");
  const [abrindo, setAbrindo] = useState(false);
  const [erroAbertura, setErroAbertura] = useState("");
  // Só o backend sabe se a ficha é PF ou PJ; até a sondagem responder não dá
  // para rotular o campo (rotular "CPF" por padrão mentia em toda ficha PJ).
  const [tipoDocumento, setTipoDocumento] = useState(null);
  // Quantidade de dígitos pedida — também vem da sondagem, para o front não
  // manter um número próprio que possa divergir do que o backend valida.
  const [digitosChave, setDigitosChave] = useState(5);
  // CNPJ alfanumérico e documento estrangeiro podem ter letras nos primeiros caracteres.
  const unidadeChave = tipoDocumento !== "CPF" ? "caracteres" : "dígitos";

  // ── Estado da ficha ────────────────────────────────────────────────────────
  const [contexto, setContexto] = useState(null);
  const [fichaId, setFichaId] = useState(null);
  const [form, setForm] = useState(null);
  const [docs, setDocs] = useState(EMPTY_DOCS);
  const [justificativas, setJustificativas] = useState({});
  const [uploadState, setUploadState] = useState(EMPTY_UPLOAD_STATE);
  const [uploadando, setUploadando] = useState({});
  const [errosForm, setErrosForm] = useState(EMPTY_ERROS);
  const [salvando, setSalvando] = useState(false);
  // Depois de enviar, a tela troca o formulário por uma confirmação — a pessoa
  // externa não precisa ver de novo o que acabou de mandar.
  const [enviado, setEnviado] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [bloqueio, setBloqueio] = useState("");
  const [snack, setSnack] = useState({ aberto: false, msg: "", severity: "success" });
  // Controle de salvamento da FC Externa.
  // Não controla a autenticação e não renova a sessão.
  const salvamentoEmAndamentoRef = useRef(false);

  // Permite aguardar um salvamento ja iniciado antes da finalizacao.
  const promessaSalvamentoRef = useRef(null);

  // Indica a conclusao completa de salvarRascunho,
  // incluindo o tratamento de erros e a liberacao do bloqueio.
  const conclusaoSalvamentoRef = useRef(null);

  // Controle das operacoes de documentos.
  // Impede a finalizacao enquanto uploads ou exclusoes
  // ainda estiverem pendentes.
  const operacoesDocumentosRef = useRef(0);
  // Identifica uploads pendentes sem depender do ciclo de renderizacao.
  const uploadsEmAndamentoRef = useRef(new Set());

  // Registra falhas de upload ou exclusao ainda nao resolvidas.
  // Uma fila vazia nao significa necessariamente que tudo foi salvo.
  const falhasDocumentosRef = useRef(new Set());
  // Guarda a ultima listagem de documentos confirmada pelo servidor.
  // Sera utilizada para reconciliar falhas antes do envio definitivo.
  const documentosConfirmadosRef = useRef(null);

  // Mantem os dados das tentativas de upload com resultado incerto.
  // Ajuda a identificar arquivos que precisam de conferencia manual
  // sem repetir automaticamente o envio.
  const tentativasUploadRef = useRef(new Map());

  // Promessa da ultima operacao enfileirada.
  // Permite executar gravacoes na ordem de solicitacao.
  const filaGravacoesRef = useRef(Promise.resolve());

  // Bloqueia novas operacoes quando a finalizacao comecar.
  const finalizacaoEmAndamentoRef = useRef(false);

  const [finalizando, setFinalizando] = useState(false);
  const [erroFinalizacao, setErroFinalizacao] = useState("");

  // Adiciona uma operacao a fila sem interromper as seguintes
  // caso uma requisicao anterior falhe.
  const enfileirarGravacao = useCallback((operacao) => {
    const proxima = filaGravacoesRef.current.then(operacao);

    filaGravacoesRef.current = proxima.catch(() => {});

    return proxima;
  }, []);

  const ultimoRascunhoSalvoRef = useRef({
    formulario: null,
    justificativas: null,
  });

  const estadoRascunhoAtualRef = useRef({
    formulario: null,
    justificativas: [],
  });

  const salvarRascunhoRef = useRef(null);

  const [estadoSalvamento, setEstadoSalvamento] = useState("");

  // Controle de inatividade da sessão pública.
  const INATIVIDADE_MS = 15 * 60 * 1000;
  const INTERVALO_ATIVIDADE_MS = 10 * 1000;

  const ultimaInteracaoRef = useRef(Date.now());
  const ultimaAtividadeEnviadaRef = useRef(Date.now());
  const atividadeEmAndamentoRef = useRef(false);

  const [sessaoExpirada, setSessaoExpirada] = useState(false);

  const mostrarSnack = useCallback((msg, severity = "success") => setSnack({ aberto: true, msg, severity }), []);

  const cabecalhos = useCallback(
    (extra = {}) => ({
      "X-Ficha-Sessao": sessao,
      ...extra,
    }),
    [sessao],
  );

  // Encerra a sessão visual sem descartar o formulário local.
  const bloquearSessao = useCallback(() => {
    setSessao("");
    setSessaoExpirada(true);
    setErroAbertura("Sua sessão expirou após 15 minutos de inatividade. " + "Informe novamente os cinco primeiros caracteres do CPF/CNPJ para continuar.");
  }, []);

  // Apenas eventos reais da interface chamam esta função.
  // O autosave não utiliza este mecanismo.
  const registrarInteracao = useCallback(
    (evento) => {
      if (!sessao || enviado || bloqueio) return;

      if (evento?.nativeEvent?.isTrusted === false) return;

      const agora = Date.now();

      // Não aceitar uma interação tardia como renovação
      // de uma sessão que já ficou inativa por 15 minutos.
      if (agora - ultimaInteracaoRef.current >= INATIVIDADE_MS) {
        bloquearSessao();
        return;
      }

      ultimaInteracaoRef.current = agora;

      // Limita o tráfego mesmo durante digitação contínua.
      if (atividadeEmAndamentoRef.current || agora - ultimaAtividadeEnviadaRef.current < INTERVALO_ATIVIDADE_MS) {
        return;
      }

      atividadeEmAndamentoRef.current = true;

      fetch(`${BASE}/${token}/atividade`, {
        method: "POST",
        headers: {
          "X-Ficha-Sessao": sessao,
        },
      })
        .then(async (resposta) => {
          const dados = await resposta.json().catch(() => ({}));

          if (!resposta.ok) {
            if (resposta.status === 401 && dados.motivo === "sessao_expirada") {
              setSessao((atual) => (atual === sessao ? "" : atual));
              setSessaoExpirada(true);
              setErroAbertura("Sua sessão expirou. Autentique-se novamente para continuar.");
            }

            return;
          }

          ultimaAtividadeEnviadaRef.current = Date.now();
        })
        .catch(() => {
          // Falha de conexão não deve ser tratada como
          // confirmação de renovação da sessão.
        })
        .finally(() => {
          atividadeEmAndamentoRef.current = false;
        });
    },
    [sessao, enviado, bloqueio, token, bloquearSessao],
  );

  useEffect(() => {
    if (!sessao || enviado) return;

    // Nova autenticação inicia um novo prazo.
    ultimaInteracaoRef.current = Date.now();
    ultimaAtividadeEnviadaRef.current = Date.now();
    atividadeEmAndamentoRef.current = false;

    setSessaoExpirada(false);

    const intervalo = setInterval(() => {
      const tempoSemInteracao = Date.now() - ultimaInteracaoRef.current;

      if (tempoSemInteracao >= INATIVIDADE_MS) {
        bloquearSessao();
      }
    }, 1000);

    return () => clearInterval(intervalo);
  }, [sessao, enviado, bloquearSessao]);

  // ── Sondagem inicial: descobre se o segundo fator é CPF ou CNPJ ────────────
  // Requisição sem a chave; o backend responde 401 com `tipoDocumento` e não
  // conta como tentativa errada. Também é aqui que link inválido, ficha sem
  // documento e fora da janela aparecem antes de pedir qualquer coisa.
  useEffect(() => {
    let ativo = true;

    fetch(`${BASE}/${token}/abrir`, { method: "POST" })
      .then(async (res) => {
        const dados = await res.json().catch(() => ({}));
        if (!ativo) return;
        if (dados.tipoDocumento) setTipoDocumento(dados.tipoDocumento);
        if (dados.digitos) setDigitosChave(dados.digitos);
        if (res.status === 404 || dados.motivo === "fora_da_janela" || dados.motivo === "sem_documento") {
          setBloqueio(dados.erro || "Link inválido ou expirado.");
        }
      })
      .catch(() => {
        if (ativo) setBloqueio("Erro de conexão. Recarregue a página.");
      });

    return () => {
      ativo = false;
    };
  }, [token]);

  // ── Abertura: valida o segundo fator antes de montar o formulário ──────────
  const handleAbrir = async (e) => {
    e?.preventDefault?.();
    const digitos = chave.toUpperCase().replace(/[^0-9A-Z]/g, "");
    if (digitos.length !== digitosChave) {
      setErroAbertura(`Informe os ${digitosChave} primeiros ${unidadeChave}.`);
      return;
    }

    setAbrindo(true);
    setErroAbertura("");
    try {
      const res = await fetch(`${BASE}/${token}/abrir`, { method: "POST", headers: { "X-Ficha-Chave": digitos } });
      const dados = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (dados.tipoDocumento) setTipoDocumento(dados.tipoDocumento);
        // Fora da janela / sem documento não são erro de digitação: a tela passa
        // a mostrar a mensagem definitiva, sem o campo de chave.
        if (dados.motivo === "fora_da_janela" || dados.motivo === "sem_documento") setBloqueio(dados.erro);
        else setErroAbertura(dados.erro || "Não foi possível abrir o link.");
        return;
      }

      if (!dados.sessao) {
        setErroAbertura("O servidor não retornou uma sessão válida.");
        return;
      }

      setSessao(dados.sessao);
      setChave("");
    } catch {
      setErroAbertura("Erro de conexão. Tente novamente.");
    } finally {
      setAbrindo(false);
    }
  };

  // ── Carrega a ficha depois que a chave foi aceita ──────────────────────────
  useEffect(() => {
    if (!sessao) return;

    let ativo = true;

    setCarregando(true);

    fetch(`${BASE}/${token}`, {
      headers: { "X-Ficha-Sessao": sessao },
    })
      .then(async (res) => {
        const dados = await res.json().catch(() => ({}));
        if (!ativo) return;
        if (!res.ok) {
          if (res.status === 401 && dados.motivo === "sessao_expirada") {
            setSessao("");
            setErroAbertura("Sua sessão expirou. Informe novamente os cinco primeiros caracteres do CPF/CNPJ.");
            return;
          }

          setBloqueio(dados.erro || "Não foi possível carregar a ficha.");
          return;
        }
        const cache = recuperarCache(token);

        const formularioCarregado = cache?.formulario
          ? {
              ...fichaParaForm(dados.ficha),
              ...cache.formulario,
            }
          : fichaParaForm(dados.ficha);

        const mapa = {};

        (dados.ficha.justificativasDocumentos ?? []).forEach((j) => {
          mapa[j.tipoDocumento] = j.justificativa;
        });

        // Na primeira abertura, o banco é a referência do rascunho.
        // Após reautenticação, não descartar alterações locais pendentes.
        const existeFormularioPendente =
          ultimoRascunhoSalvoRef.current.formulario !== null &&
          estadoRascunhoAtualRef.current.formulario !== null &&
          JSON.stringify(estadoRascunhoAtualRef.current.formulario) !== ultimoRascunhoSalvoRef.current.formulario;

        const existeJustificativaPendente =
          ultimoRascunhoSalvoRef.current.justificativas !== null &&
          JSON.stringify(estadoRascunhoAtualRef.current.justificativas) !== ultimoRascunhoSalvoRef.current.justificativas;

        setContexto(dados);
        setFichaId(dados.ficha.id);
        const documentosServidor = dados.ficha.documentos ?? EMPTY_DOCS;

        const documentosLocais = await listarDocumentosLocais(token);

        if (!ativo) return;

        setDocs([
          ...documentosServidor,
          ...documentosLocais.map((documento) => ({
            id: documento.id,
            tipo: documento.tipo,
            obrigatorio: documento.obrigatorio,
            arquivo: documento.arquivo,
            data: documento.data,
            local: true,
          })),
        ]);

        if (!existeFormularioPendente) {
          setForm(formularioCarregado);
          ultimoRascunhoSalvoRef.current.formulario = JSON.stringify(formularioCarregado);
        }

        if (!existeJustificativaPendente) {
          setJustificativas(cache?.justificativas || mapa);
        }

        // A referência de justificativas será inicializada após
        // o checklist identificar os documentos ainda ausentes.
      })
      .catch(() => ativo && setBloqueio("Erro de conexão ao carregar a ficha."))
      .finally(() => ativo && setCarregando(false));

    return () => {
      ativo = false;
    };
  }, [sessao, token]);

  const recarregarDocs = useCallback(async () => {
    if (!sessao) {
      throw new Error("Sessão indisponível para atualizar documentos.");
    }

    const resposta = await fetch(`${BASE}/${token}/documentos`, {
      headers: { "X-Ficha-Sessao": sessao },
    });

    if (!resposta.ok) {
      throw new Error("Não foi possível atualizar os documentos.");
    }

    const dados = await resposta.json();
    if (!Array.isArray(dados)) {
      throw new Error("O servidor retornou uma lista de documentos inválida.");
    }

    const documentosServidor = dados;

    const documentosLocais = await listarDocumentosLocais(token);

    const documentosAtualizados = [
      ...documentosServidor,
      ...documentosLocais.map((documento) => ({
        id: documento.id,
        tipo: documento.tipo,
        obrigatorio: documento.obrigatorio,
        arquivo: documento.arquivo,
        data: documento.data,
        local: true,
      })),
    ];

    // Apenas os documentos efetivamente persistidos
    // são considerados confirmados pelo servidor.
    documentosConfirmadosRef.current = documentosServidor;

    setDocs(documentosAtualizados);

    return documentosServidor;
  }, [token, sessao]);
  // Reconcilia falhas de exclusao usando uma listagem
  // confirmada diretamente pelo servidor.
  // Nao reenvia DELETE e nao remove falhas de upload.
  const reconciliarFalhasExclusao = useCallback(async () => {
    if (!sessao) {
      throw new Error("Sessão indisponível para conferir os documentos.");
    }

    const documentosAtuais = await recarregarDocs();

    for (const chaveFalha of Array.from(falhasDocumentosRef.current)) {
      if (!chaveFalha.startsWith("exclusao_")) {
        continue;
      }

      const docId = chaveFalha.slice("exclusao_".length);

      const documentoAindaExiste = documentosAtuais.some((documento) => String(documento.id) === docId);

      if (!documentoAindaExiste) {
        falhasDocumentosRef.current.delete(chaveFalha);
      }
    }

    return documentosAtuais;
  }, [sessao, recarregarDocs]);

  // ── Props do FichaPessoaForm ───────────────────────────────────────────────

  const setField = useCallback((campo, valor) => {
    if (finalizacaoEmAndamentoRef.current) return;

    setForm((prev) => (prev ? { ...prev, [campo]: valor } : prev));
  }, []);

  const setUploadField = useCallback((fId, tipo, campo, valor) => {
    setUploadState((prev) => ({
      ...prev,
      [fId]: { ...(prev[fId] ?? {}), [tipo]: { ...(prev[fId]?.[tipo] ?? { arquivo: null, nome: "" }), [campo]: valor } },
    }));
  }, []);

  const handleUploadDoc = useCallback(
    async (fId, tipo, obrigatorio) => {
      if (!sessao || enviado || bloqueio || finalizacaoEmAndamentoRef.current) {
        mostrarSnack("Não é possível adicionar documentos neste momento.", "warning");
        return;
      }

      const uState = uploadState[fId]?.[tipo];

      if (!uState?.arquivo) {
        mostrarSnack("Selecione um arquivo.", "warning");
        return;
      }

      const arquivo = uState.arquivo;
      const id = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const documentoLocal = {
        id,
        token,
        fichaId: fId,
        tipo,
        obrigatorio: Boolean(obrigatorio),
        arquivo: uState.nome?.trim() || arquivo.name,
        nomeArquivo: uState.nome?.trim() || arquivo.name,
        data: new Date().toISOString(),
        conteudo: arquivo,
        local: true,
      };

      const chaveUpload = `${fId}_${tipo}`;

      setUploadando((anterior) => ({
        ...anterior,
        [chaveUpload]: true,
      }));

      try {
        await salvarDocumentoLocal(documentoLocal);

        setDocs((anterior) => [
          ...anterior,
          {
            id: documentoLocal.id,
            tipo: documentoLocal.tipo,
            obrigatorio: documentoLocal.obrigatorio,
            arquivo: documentoLocal.arquivo,
            data: documentoLocal.data,
            local: true,
          },
        ]);

        setUploadField(fId, tipo, "arquivo", null);
        setUploadField(fId, tipo, "nome", "");

        mostrarSnack("Documento armazenado no navegador.");
      } catch (erro) {
        console.error("Erro ao armazenar documento:", erro);
        mostrarSnack("Não foi possível armazenar o documento no navegador.", "error");
      } finally {
        setUploadando((anterior) => ({
          ...anterior,
          [chaveUpload]: false,
        }));
      }
    },
    [sessao, enviado, bloqueio, uploadState, token, mostrarSnack, setUploadField],
  );

  const handleDeleteDoc = useCallback(
    async (fId, docId) => {
      if (!sessao || enviado || bloqueio || finalizacaoEmAndamentoRef.current) {
        mostrarSnack("Não é possível excluir documentos neste momento.", "warning");
        return;
      }

      const idDocumento = String(docId);

      // Documento temporário: excluir apenas do navegador.
      if (idDocumento.startsWith("local-")) {
        try {
          await excluirDocumentoLocal(idDocumento);

          setDocs((anterior) => anterior.filter((documento) => String(documento.id) !== idDocumento));

          mostrarSnack("Documento temporário removido.");
        } catch (erro) {
          console.error("Erro ao remover documento temporário:", erro);

          mostrarSnack("Não foi possível remover o documento temporário.", "error");
        }

        return;
      }

      // Documento já persistido no servidor:
      // não executar DELETE antes do envio definitivo.
      mostrarSnack(
        "Documentos já gravados no sistema não podem ser excluídos " + "durante o preenchimento externo. Solicite a alteração " + "ao responsável pela ficha.",
        "warning",
      );
    },
    [sessao, enviado, bloqueio, mostrarSnack],
  );

  const baixarBlob = useCallback(
    async (docId) => {
      const idDocumento = String(docId);

      // Arquivo armazenado no IndexedDB.
      if (idDocumento.startsWith("local-")) {
        const documento = await buscarDocumentoLocal(idDocumento);

        if (!documento || !documento.conteudo) {
          throw new Error("Documento temporário não encontrado no navegador.");
        }

        return documento.conteudo;
      }

      // Arquivo que já existe no servidor.
      const resposta = await fetch(`${BASE}/${token}/documentos/${idDocumento}/download`, {
        headers: cabecalhos(),
      });

      if (!resposta.ok) {
        throw new Error("Não foi possível recuperar o documento do servidor.");
      }

      return resposta.blob();
    },
    [token, cabecalhos],
  );

  const handleDownloadDoc = useCallback(
    (fId, docId, nomeArquivo) => {
      baixarBlob(docId)
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = nomeArquivo;
          a.click();
          URL.revokeObjectURL(url);
        })
        .catch(() => mostrarSnack("Erro ao baixar arquivo.", "error"));
    },
    [baixarBlob, mostrarSnack],
  );

  const handleVisualizarDoc = useCallback(
    (fId, docId) => {
      baixarBlob(docId)
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank");
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        })
        .catch(() => mostrarSnack("Erro ao visualizar arquivo.", "error"));
    },
    [baixarBlob, mostrarSnack],
  );

  const docsPorFicha = useMemo(() => (fichaId ? { [fichaId]: docs } : EMPTY_UPLOAD_STATE), [fichaId, docs]);

  // Mesmo critério de checklist da tela autenticada. `garantiaImobiliaria` chega
  // do backend como booleano derivado — a proposta em si não é exposta aqui.
  const checklistAtual = useMemo(() => {
    if (!form || !contexto) return [];
    const ehFiador = form.campoProposta === "Fiador";
    const ehLocatario = form.campoProposta === "Locataria";
    const ehPJ = form.tipoPessoa === "PJ";
    // Matrícula/IPTU: PARA FINS DE GARANTIA quando a proposta indica imóvel na garantia
    const paraFins = (ehLocatario && contexto.garantiaImobiliaria) || (ehFiador && contexto.fiadorComImovel);
    let lista = [];
    if (ehFiador && ehPJ) lista = CHECKLIST_FIADOR_PJ;
    else if (ehFiador && !ehPJ) lista = CHECKLIST_FIADOR_PF;
    else if (ehLocatario && !ehPJ && contexto.garantiaImobiliaria) lista = CHECKLIST_LOCATARIO_PF_IMOBILIARIA;
    else if (ehLocatario && ehPJ) lista = CHECKLIST_LOCATARIO_PJ_PADRAO;
    else if (ehLocatario && !ehPJ) lista = CHECKLIST_LOCATARIO_PF_PADRAO;
    return aplicarFinsGarantia(lista, paraFins);
  }, [form, contexto]);

  const mostrarLinkGoogleChecklist = useMemo(() => {
    if (!form || !contexto) return false;
    const ehFiador = form.campoProposta === "Fiador";
    const ehLocatario = form.campoProposta === "Locataria";
    return (ehFiador && contexto.fiadorComImovel) || (ehLocatario && contexto.garantiaImobiliaria);
  }, [form, contexto]);

  // ── Documentos ausentes → justificativa (mesma lista que o interno cobra) ──
  const documentosAusentes = useMemo(() => {
    if (!form || !contexto) return [];
    const grupos = obterGruposChecklistDocumentosFicha(form, {
      garantia: contexto.garantiaImobiliaria ? "IMOBILIARIA" : "",
      fiadorTipoGarantia: contexto.fiadorComImovel ? "COM_IMOVEL" : "",
    });
    const ausentes = [];
    grupos.forEach((grupo) =>
      grupo.itens.forEach((item) => {
        if (!docs.some((d) => String(d.tipo) === String(item.tipo))) {
          ausentes.push({ grupo: grupo.titulo, tipo: item.tipo, label: item.label });
        }
      }),
    );
    return ausentes;
  }, [form, contexto, docs]);
  // ── Rascunho automático ────────────────────────────────────────────────────

  // Mesma lista utilizada no envio manual.
  // O backend compartilhado espera o formulário completo.
  const justificativasParaSalvar = useMemo(
    () =>
      documentosAusentes
        .filter((doc) => String(justificativas[doc.tipo] || "").trim())
        .map((doc) => ({
          tipoDocumento: doc.tipo,
          justificativa: String(justificativas[doc.tipo]).trim(),
        })),
    [documentosAusentes, justificativas],
  );

  // Mantem a versao atual dos dados disponivel para o salvamento.
  // A referencia acompanha os valores desta renderizacao.
  estadoRascunhoAtualRef.current = {
    formulario: form,
    justificativas: justificativasParaSalvar,
    justificativasOriginais: justificativas,
  };

  // Define a referência inicial das justificativas carregadas do banco.
  useEffect(() => {
    if (!form || !contexto || !sessao) return;

    if (ultimoRascunhoSalvoRef.current.justificativas === null) {
      ultimoRascunhoSalvoRef.current.justificativas = JSON.stringify(justificativasParaSalvar);
    }
  }, [form, contexto, sessao, justificativasParaSalvar]);

  const salvarRascunho = useCallback(
    async ({ automatico = false, paraFinalizacao = false } = {}) => {
      if (!sessao || enviado || bloqueio || (finalizacaoEmAndamentoRef.current && !paraFinalizacao)) {
        return false;
      }

      const atual = estadoRascunhoAtualRef.current;

      if (!atual.formulario) return false;

      setEstadoSalvamento("Salvando rascunho...");

      const salvo = gravarCache(token, atual.formulario, atual.justificativasOriginais || {});

      if (salvo) {
        ultimoRascunhoSalvoRef.current.formulario = JSON.stringify(atual.formulario);

        ultimoRascunhoSalvoRef.current.justificativas = JSON.stringify(atual.justificativas);

        setEstadoSalvamento("Rascunho salvo no navegador.");
        return true;
      }

      setEstadoSalvamento("Não foi possível salvar o rascunho.");

      if (!automatico) {
        mostrarSnack("Não foi possível salvar o rascunho.", "error");
      }

      return false;
    },
    [sessao, enviado, bloqueio, token, mostrarSnack],
  );

  // Aguarda a conclusao real do salvamento anterior.
  // Nao cria outra operacao na fila de gravacoes.
  const aguardarSalvamentoPendente = useCallback(async () => {
    const conclusaoPendente = conclusaoSalvamentoRef.current;

    if (!conclusaoPendente) {
      return !salvamentoEmAndamentoRef.current;
    }

    try {
      const resultado = await conclusaoPendente;

      return resultado === true && !salvamentoEmAndamentoRef.current;
    } catch {
      return false;
    }
  }, []);
  // Confere se as operacoes anteriores terminaram e se existem
  // documentos com resultado pendente de confirmacao.
  // Nao executa o envio definitivo.
  const conferirPendenciasAntesDaFinalizacao = useCallback(async () => {
    const salvamentoConcluido = await aguardarSalvamentoPendente();

    if (!salvamentoConcluido) {
      throw new Error("Não foi possível confirmar o encerramento do salvamento anterior.");
    }

    // Aguarda as gravacoes de documentos ja enfileiradas.
    await filaGravacoesRef.current;

    // Uma fila resolvida nao garante que os handlers tenham
    // concluido seus controles de sucesso ou falha.
    if (operacoesDocumentosRef.current > 0 || uploadsEmAndamentoRef.current.size > 0) {
      throw new Error("Ainda existem operações de documentos em andamento. Aguarde e tente novamente.");
    }

    // Reconsulta o servidor para reconciliar exclusoes
    // sem repetir automaticamente nenhuma requisicao DELETE.
    const documentosConfirmados = await reconciliarFalhasExclusao();

    if (falhasDocumentosRef.current.size > 0) {
      throw new Error("Existem operações de documentos sem confirmação. Confira os anexos antes de enviar definitivamente.");
    }

    return documentosConfirmados;
  }, [aguardarSalvamentoPendente, reconciliarFalhasExclusao]);

  // Confere as operacoes pendentes e salva a ultima versao
  // do formulario antes de permitir o envio definitivo.
  // Nao executa POST /enviar.
  const prepararSalvamentoFinal = useCallback(async () => {
    if (!sessao || enviado || bloqueio || !estadoRascunhoAtualRef.current.formulario) {
      throw new Error("A ficha não está disponível para finalização.");
    }

    // Esta funcao deve ser chamada somente depois de bloquear
    // novas edicoes e operacoes com documentos.
    if (!finalizacaoEmAndamentoRef.current) {
      throw new Error("A finalização ainda não foi iniciada.");
    }

    await conferirPendenciasAntesDaFinalizacao();

    // Executa fora da fila: salvarRascunho ja enfileira
    // internamente as requisicoes necessarias.
    const sucesso = await salvarRascunho({
      paraFinalizacao: true,
    });

    if (!sucesso) {
      throw new Error("Não foi possível confirmar o salvamento final da ficha. Confira os dados e tente novamente.");
    }

    return true;
  }, [sessao, enviado, bloqueio, conferirPendenciasAntesDaFinalizacao, salvarRascunho]);

  // O intervalo não é reiniciado a cada tecla digitada.
  useEffect(() => {
    salvarRascunhoRef.current = salvarRascunho;
  }, [salvarRascunho]);

  useEffect(() => {
    if (!sessao || !form || enviado) return;

    const intervalo = setInterval(() => {
      salvarRascunhoRef.current?.({ automatico: true });
    }, 60 * 1000);

    return () => clearInterval(intervalo);
  }, [sessao, Boolean(form), enviado]);

  // ── Salvar ─────────────────────────────────────────────────────────────────
  const handleSalvar = async () => {
    if (finalizacaoEmAndamentoRef.current) return;

    if (salvamentoEmAndamentoRef.current) {
      mostrarSnack("Um salvamento já está em andamento. Aguarde alguns instantes.", "warning");
      return;
    }

    setSalvando(true);

    try {
      const sucesso = await salvarRascunho();

      if (sucesso) {
        mostrarSnack("Rascunho salvo com sucesso.");
      }
    } finally {
      setSalvando(false);
    }
  };

  // Envio irreversivel.
  // Nao repetir automaticamente um POST com resultado incerto.
  const handleEnviarDefinitivamente = async () => {
    if (finalizacaoEmAndamentoRef.current || !sessao || enviado || bloqueio) {
      return;
    }

    // Impede um segundo clique e novas operacoes documentais.
    finalizacaoEmAndamentoRef.current = true;

    setFinalizando(true);
    setErroFinalizacao("");

    let envioIniciado = false;
    let podeLiberarEdicao = true;

    try {
      // Primeiro solicita a confirmação. Nenhuma gravação deve ocorrer antes.
      const confirmou = window.confirm("Enviar definitivamente esta Ficha de Cadastro? " + "Após o envio, não será possível alterá-la por este link.");

      if (!confirmou) {
        return;
      }

      // Confere as operações pendentes e preserva o rascunho local.
      await prepararSalvamentoFinal();

      // Ultima verificacao antes de enviar.
      if (
        !sessao ||
        enviado ||
        bloqueio ||
        operacoesDocumentosRef.current > 0 ||
        uploadsEmAndamentoRef.current.size > 0 ||
        falhasDocumentosRef.current.size > 0 ||
        salvamentoEmAndamentoRef.current
      ) {
        throw new Error("Existem operações pendentes. " + "O envio foi cancelado; confira a ficha.");
      }

      const atual = estadoRascunhoAtualRef.current;

      if (!atual.formulario) {
        throw new Error("Os dados da ficha não estão disponíveis para envio.");
      }

      const documentosPendentes = await listarDocumentosLocais(token);

      if (documentosPendentes.length > 0) {
        throw new Error(
          "Os documentos estão preservados neste navegador. " +
            "A finalização com anexos estará disponível após a " +
            "integração do envio dos arquivos ao servidor.",
        );
      }

      const dadosEnvio = {
        formulario: atual.formulario,
        justificativas: atual.justificativas,
      };

      envioIniciado = true;

      const resposta = await fetch(`${BASE}/${token}/enviar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Ficha-Sessao": sessao,
        },
        body: JSON.stringify(dadosEnvio),
      });

      const dados = await resposta.json().catch(() => ({}));

      if (!resposta.ok) {
        const erro = new Error(dados.erro || "Não foi possível confirmar o envio definitivo.");

        erro.status = resposta.status;
        erro.motivo = dados.motivo;

        // Essas respostas indicam rejeicao explicita.
        if ([400, 401, 403, 404, 422].includes(resposta.status)) {
          envioIniciado = false;
        }

        throw erro;
      }

      podeLiberarEdicao = false;

      try {
        limparCache(token);

        // Limpeza após confirmação de sucesso do servidor.
        await limparDocumentosLocais(token);
      } catch (erro) {
        console.error("Não foi possível limpar todos os dados locais da ficha:", erro);
      }

      setEnviado(true);
      setEstadoSalvamento("Ficha enviada definitivamente.");
    } catch (erro) {
      if (envioIniciado) {
        // Em falha de rede, resposta incerta ou erro 5xx,
        // nao presumir que a operacao falhou.
        podeLiberarEdicao = false;

        setErroFinalizacao(
          "Não foi possível confirmar o resultado do envio. " +
            "Não tente enviar novamente. " +
            "Consulte o responsável pela ficha e verifique " +
            "o status no sistema.",
        );
      } else {
        setErroFinalizacao(erro?.message || "Não foi possível preparar a ficha para envio.");

        if (erro?.status === 401 && erro?.motivo === "sessao_expirada") {
          setSessao("");

          setErroAbertura("Sua sessão expirou. " + "Autentique-se novamente para continuar.");
        }
      }
    } finally {
      if (podeLiberarEdicao) {
        finalizacaoEmAndamentoRef.current = false;
        setFinalizando(false);
      }
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const moldura = (conteudo) => (
    <ThemeProvider theme={muiTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "var(--ig-f-15171c)",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          p: { xs: 1.5, sm: 4 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1100 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <img src={logo} alt="" style={{ height: 44 }} />
            <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 700, fontSize: "1.1rem" }}>Ficha de Cadastro</Typography>
          </Box>
          {conteudo}
        </Box>
      </Box>
    </ThemeProvider>
  );

  const cartao = (filhos) => (
    <Paper sx={{ p: 3, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, boxShadow: "0 0 20px rgba(0,0,0,0.4)" }}>{filhos}</Paper>
  );

  if (bloqueio) {
    return moldura(
      cartao(
        <>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600, mb: 1 }}>Link indisponível</Typography>
          <Typography sx={{ color: "var(--ig-t-cccccc)" }}>{bloqueio}</Typography>
          <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.85rem", mt: 2 }}>Em caso de dúvida, fale com quem enviou este link.</Typography>
        </>,
      ),
    );
  }

  if (enviado) {
    return moldura(
      cartao(
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: "flex-start" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 34 }} />
            <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 700, fontSize: "1.2rem" }}>Dados enviados com sucesso</Typography>
          </Box>
          <Typography sx={{ color: "var(--ig-t-cccccc)" }}>Obrigado! Suas informações e documentos foram recebidos.</Typography>

          <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.85rem", mt: 1 }}>
            O envio foi concluído definitivamente. Não é possível alterar ou complementar esta ficha por este link.
          </Typography>

          <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.85rem" }}>Você já pode fechar esta página.</Typography>
        </Box>,
      ),
    );
  }

  if (!sessao) {
    if (sessaoExpirada && !tipoDocumento) {
      return moldura(cartao(<Typography sx={{ color: "var(--ig-t-ffffff)" }}>Sessão expirada. Recarregue a página para autenticar novamente.</Typography>));
    }
    if (!tipoDocumento) return moldura(cartao(<CircularProgress size={28} sx={{ color: "#1998a8" }} />));

    return moldura(
      cartao(
        <Box component="form" onSubmit={handleAbrir} sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 420 }}>
          <Typography sx={{ color: "var(--ig-t-cccccc)" }}>
            Para abrir a ficha, informe os{" "}
            <strong style={{ color: "var(--ig-t-ffffff)" }}>
              {digitosChave} primeiros {unidadeChave}
            </strong>{" "}
            do {tipoDocumento} desta ficha.
          </Typography>
          <TextField
            label={`${digitosChave} primeiros ${unidadeChave} do ${tipoDocumento}`}
            size="small"
            autoFocus
            value={chave}
            onChange={(e) =>
              setChave(
                (tipoDocumento !== "CPF" ? e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "") : e.target.value.replace(/\D/g, "")).slice(0, digitosChave),
              )
            }
            slotProps={{ htmlInput: { inputMode: tipoDocumento !== "CPF" ? "text" : "numeric", maxLength: digitosChave } }}
            error={!!erroAbertura}
            helperText={erroAbertura}
            sx={{ ...inputSx(false, false, !!erroAbertura), maxWidth: 260 }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={abrindo}
            sx={{ width: "auto", alignSelf: "flex-start", backgroundColor: "#1998a8", "&:hover": { backgroundColor: "#1565c0" }, fontWeight: "bold", px: 4 }}
          >
            {abrindo ? <CircularProgress size={20} sx={{ color: "var(--ig-t-ffffff)" }} /> : "Abrir ficha"}
          </Button>
        </Box>,
      ),
    );
  }

  if (carregando || !form) {
    return moldura(cartao(<CircularProgress size={28} sx={{ color: "#1998a8" }} />));
  }

  return moldura(
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
      onKeyDownCapture={registrarInteracao}
      onPointerDownCapture={registrarInteracao}
      onChangeCapture={registrarInteracao}
      onWheelCapture={registrarInteracao}
      onScrollCapture={registrarInteracao}
    >
      {cartao(
        <>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600, fontSize: "1.05rem" }}>{contexto.papelLabel}</Typography>
          <Typography sx={{ color: "var(--ig-t-aaaaaa)", fontSize: "0.87rem" }}>
            {contexto.imovelApelido ? `Imóvel: ${contexto.imovelApelido}` : "Preencha seus dados e envie os documentos solicitados."}
          </Typography>
        </>,
      )}

      {cartao(
        <Box
          component="fieldset"
          disabled={finalizando}
          sx={{
            border: 0,
            p: 0,
            m: 0,
            minWidth: 0,
          }}
        >
          <FichaPessoaForm
            fichaAtual={form}
            fichaId={fichaId}
            ro={false}
            ehAprovador={false}
            setField={setField}
            errosForm={errosForm}
            setErrosForm={setErrosForm}
            buscandoCnpj={false}
            handleCnpjChange={undefined}
            primeiroCampoRef={undefined}
            checklistAtual={checklistAtual}
            docsPorFicha={docsPorFicha}
            uploadState={uploadState}
            uploadando={uploadando}
            mostrarLinkGoogleChecklist={mostrarLinkGoogleChecklist}
            linkGoogleImovel={form.linkGoogleImovel || ""}
            setUploadField={setUploadField}
            handleUploadDoc={handleUploadDoc}
            handleDeleteDoc={handleDeleteDoc}
            handleVisualizarDoc={handleVisualizarDoc}
            handleDownloadDoc={handleDownloadDoc}
          />
        </Box>,
      )}

      {documentosAusentes.length > 0 &&
        cartao(
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Documentos não enviados</Typography>
            <Typography sx={{ color: "var(--ig-t-aaaaaa)", fontSize: "0.85rem" }}>
              Se você não tem algum dos documentos abaixo, explique o motivo. Documentos enviados saem desta lista automaticamente.
            </Typography>
            {documentosAusentes.map((doc) => (
              <TextField
                key={doc.tipo}
                label={doc.label}
                size="small"
                fullWidth
                multiline
                minRows={1}
                placeholder="Motivo de não possuir este documento"
                value={justificativas[doc.tipo] ?? ""}
                disabled={finalizando}
                onChange={(e) => {
                  if (finalizacaoEmAndamentoRef.current) return;

                  const valor = e.target.value;

                  setJustificativas((prev) => ({
                    ...prev,
                    [doc.tipo]: valor,
                  }));
                }}
                sx={inputSx(false)}
              />
            ))}
          </Box>,
        )}

      {cartao(
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Typography
            sx={{
              color: "var(--ig-t-aaaaaa)",
              fontSize: "0.8rem",
              mr: 2,
              alignSelf: "center",
            }}
          >
            {estadoSalvamento}
          </Typography>
          <Button
            variant="contained"
            onClick={handleSalvar}
            disabled={salvando || finalizando}
            sx={{
              width: "auto",
              backgroundColor: "#2e7d32",
              "&:hover": { backgroundColor: "#1b5e20" },
              fontWeight: "bold",
              py: 1,
              px: 4,
            }}
          >
            {salvando ? <CircularProgress size={20} sx={{ color: "var(--ig-t-ffffff)" }} /> : "Salvar rascunho"}
          </Button>

          <Button
            variant="contained"
            onClick={handleEnviarDefinitivamente}
            disabled={finalizando || salvando || !sessao || !!bloqueio}
            sx={{
              ml: 2,
              backgroundColor: "#1565c0",
              "&:hover": {
                backgroundColor: "#0d47a1",
              },
              fontWeight: "bold",
              py: 1,
              px: 3,
            }}
          >
            {finalizando ? <CircularProgress size={20} sx={{ color: "var(--ig-t-ffffff)" }} /> : "ENVIAR DEFINITIVAMENTE"}
          </Button>
        </Box>,
      )}

      {erroFinalizacao && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {erroFinalizacao}
        </Alert>
      )}

      <Snackbar
        open={snack.aberto}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, aberto: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, aberto: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>,
  );
}
